import React, { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Button,
  Group,
  Menu,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
} from "@mantine/core";
import { openConfirmModal } from "@mantine/modals";
import { IconAlertTriangle, IconCircleCheck, IconDots } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import { saveBlobDownload } from "../utils/fileDownload";
import {
  canDownloadMatchReportFile,
  formatScreenedAt,
  handleMatchLabel,
  interpretNameScreening,
  nameScreeningRecord,
  nameScreeningRowsFromRecord,
  nameScreeningType,
  nameScreeningSubjects,
  screenRecordStatusText,
} from "../helpers/nameScreening";
import { DueDiligenceBadge, DueDiligencePanel } from "./clientDueDiligencePanel";
import ClientDueDiligenceMatchReport from "./clientDueDiligenceMatchReport";

const savedRows = (form) =>
  nameScreeningRowsFromRecord(form?.values?.dueDiligence?.nameScreening);

const toDownloadFile = (file) => ({
  ...file,
  link: file?.link ?? file?.path ?? "",
  name: file?.name ?? file?.filename,
  filename: file?.filename ?? file?.name,
  mimetype: file?.mimetype ?? file?.mimeType,
});

const persistNameScreening = async (form, record) => {
  if (!form || !Array.isArray(record)) return null;
  const clientId = form.values?._id || form.values?.id;
  const updates = { "dueDiligence.nameScreening": record };
  if (clientId && typeof form.savePartialFields === "function") {
    return form.savePartialFields(updates);
  }
  if (typeof form.setFieldValue === "function") {
    form.setFieldValue("dueDiligence", {
      ...(form.values?.dueDiligence || {}),
      nameScreening: record,
    });
  }
  return null;
};

const ClientDueDiligenceNameScreening = ({ form }) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [section, setSection] = useState("screen");
  const [screening, setScreening] = useState(false);
  const [providerEnabled, setProviderEnabled] = useState(null);
  const [rows, setRows] = useState(() => {
    const saved = savedRows(form);
    return saved.length ? saved : null;
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [handleIndex, setHandleIndex] = useState(0);
  const [blockedAttempt, setBlockedAttempt] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [downloadingLogId, setDownloadingLogId] = useState("");
  const formRef = useRef(form);
  formRef.current = form;
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const screenedHere = useRef(false);
  const saveTimer = useRef(null);
  const pendingRecord = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.FwdAml.nameScreeningEnabled()
      .then((status) => {
        if (!cancelled) setProviderEnabled(!!status?.enabled);
      })
      .catch(() => {
        if (!cancelled) setProviderEnabled(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      clearTimeout(saveTimer.current);
      if (pendingRecord.current) {
        persistNameScreening(formRef.current, pendingRecord.current);
      }
    },
    []
  );

  const rootRef = useRef(null);
  const focusApplied = useRef(false);
  const scrolledToScreening = useRef(false);
  const savedKey = JSON.stringify(form?.values?.dueDiligence?.nameScreening || []);
  useEffect(() => {
    if (screenedHere.current) return;
    const saved = savedRows(formRef.current);
    setRows(saved.length ? saved : null);
  }, [savedKey]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("focus") !== "nameScreening") return;
    if (!scrolledToScreening.current && rootRef.current?.scrollIntoView) {
      scrolledToScreening.current = true;
      rootRef.current.scrollIntoView({ block: "start" });
    }
    const alertId = params.get("alertId") || "";
    if (!alertId || focusApplied.current) return;
    const saved = savedRows(formRef.current);
    const index = saved.findIndex((row) => String(row.outcome?.alertId || "") === alertId);
    if (index < 0) return;
    focusApplied.current = true;
    setSelectedIndex(index);
    setHandleIndex(index);
  }, [savedKey]);

  const screen = async () => {
    if (providerEnabled !== true) return;
    const subjects = nameScreeningSubjects(form?.values);
    if (!subjects.length || subjects.every((subject) => !subject.ready)) {
      setBlockReason(subjects.find((subject) => !subject.ready)?.reason || "name");
      setBlockedAttempt(true);
      return;
    }
    setBlockedAttempt(false);
    setBlockReason("");

    setScreening(true);
    const next = [];
    for (const subject of subjects) {
      if (!subject.ready) {
        next.push({ ...subject, outcome: null });
        continue;
      }
      try {
        const data = await api.FwdAml.nameScreening(subject.request);
        next.push({ ...subject, outcome: interpretNameScreening(data) });
      } catch (error) {
        next.push({
          ...subject,
          outcome: {
            status: "ERROR",
            errorCode: "",
            errorMessage: error?.message || "",
          },
        });
      }
    }
    const screenedAt = new Date().toISOString();
    const stamped = next.map((row) =>
      row.outcome
        ? { ...row, outcome: { ...row.outcome, screenedAt, type: "Initial" } }
        : row
    );
    const previous = (rowsRef.current || []).filter((row) => row?.outcome?.status);
    const incoming = stamped.filter((row) => row?.outcome?.status);
    const combined = [...incoming, ...previous];
    setRows(combined.length ? combined : null);
    setSelectedIndex(0);
    const firstHit = combined.findIndex((row) => row.outcome?.status === "HIT");
    if (firstHit >= 0) setHandleIndex(firstHit);
    screenedHere.current = true;
    const record = nameScreeningRecord(combined);
    const currentForm = formRef.current;
    try {
      const saved = await persistNameScreening(currentForm, record);
      if (saved && saved.success === false) {
        showNotification({
          color: "red",
          title: t("Name screening failed"),
          message: t("The screening result was not saved"),
        });
      }
    } catch (error) {
      showNotification({
        color: "red",
        title: t("Name screening failed"),
        message: t("The screening result was not saved"),
      });
    }
    setScreening(false);
  };

  const saveRecord = (nextRows, delay) => {
    const record = nameScreeningRecord(nextRows);
    pendingRecord.current = record;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      pendingRecord.current = null;
      try {
        const saved = await persistNameScreening(formRef.current, record);
        if (saved && saved.success === false) {
          showNotification({
            color: "red",
            title: t("Name screening failed"),
            message: t("The screening result was not saved"),
          });
        }
      } catch (error) {
        showNotification({
          color: "red",
          title: t("Name screening failed"),
          message: t("The screening result was not saved"),
        });
      }
    }, delay);
  };

  const updateRow = (rowIndex, outcome, delay = 400) => {
    const next = (rowsRef.current || []).map((row, index) =>
      index === rowIndex ? { ...row, outcome } : row
    );
    rowsRef.current = next;
    setRows(next);
    screenedHere.current = true;
    saveRecord(next, delay);
  };

  const updateReport = (rowIndex, report, delay = 400) => {
    const current = rowsRef.current?.[rowIndex];
    if (!current?.outcome) return;
    updateRow(rowIndex, { ...current.outcome, report }, delay);
  };

  const removeRecord = (index) => {
    openConfirmModal({
      title: t("Remove screen record"),
      children: t("Remove this screen record?"),
      labels: { confirm: t("Remove"), cancel: t("Cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        const next = (rowsRef.current || []).filter((_, rowIndex) => rowIndex !== index);
        rowsRef.current = next;
        setRows(next.length ? next : null);
        setSelectedIndex(0);
        const firstHit = next.findIndex((row) => row.outcome?.status === "HIT");
        setHandleIndex(firstHit >= 0 ? firstHit : 0);
        screenedHere.current = true;
        saveRecord(next, 0);
      },
    });
  };

  const downloadReport = async (report) => {
    const file = report?.file;
    const fileName = report?.fileName || file?.name || "match-report.pdf";
    if (!canDownloadMatchReportFile(report)) return;
    setDownloadingLogId(String(file?.link || fileName));
    try {
      const result = await api.downloadFiles(
        [toDownloadFile(file)],
        file.filename || file.name || fileName
      );
      saveBlobDownload(result.blob, result.filename || fileName);
    } catch (error) {
      showNotification({
        color: "red",
        message: t("Download failed"),
      });
    }
    setDownloadingLogId("");
  };

  const setHandling = (rowIndex, resolved) => {
    const current = rowsRef.current?.[rowIndex];
    if (!current?.outcome) return;
    const outcome = {
      ...current.outcome,
      handlingStatus: resolved ? "RESOLVED" : "PENDING",
    };
    if (resolved) outcome.resolvedAt = new Date().toISOString();
    else delete outcome.resolvedAt;
    updateRow(rowIndex, outcome, 0);
  };

  const recorded = (rows || [])
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row?.outcome?.status);
  const pendingHit = recorded.some(
    ({ row }) => row.outcome.status === "HIT" && row.outcome.handlingStatus !== "RESOLVED"
  );
  const latest = recorded[0]?.row;
  const blocked = blockedAttempt || Boolean(rows?.some((row) => !row.ready));
  const blockedReason =
    (rows || []).find((row) => !row.ready)?.reason || blockReason;
  const passed = latest?.outcome?.status === "PASS" && !pendingHit && !blocked;
  const hitEntries = recorded.filter(({ row }) => row.outcome.status === "HIT");
  const activeHandle =
    hitEntries.find((entry) => entry.index === handleIndex) || hitEntries[0] || null;
  const selectedEntry =
    recorded.find((entry) => entry.index === selectedIndex) || recorded[0] || null;
  const includeHandleName = new Set(hitEntries.map(({ row }) => row.label)).size > 1;
  const handlingResolved = activeHandle?.row.outcome.handlingStatus === "RESOLVED";

  const badge = pendingHit
    ? { color: "orange", label: t("Needs handling") }
    : passed
      ? { color: "green", label: t("Passed") }
      : null;

  const note = pendingHit
    ? t("Request further handling by Operation (any match result return)")
    : blocked
      ? screenRecordStatusText({ ready: false, reason: blockedReason }, t)
      : passed
        ? t("Pass (no match result)")
        : latest?.outcome?.status === "HIT"
          ? t("Resolved")
          : latest?.outcome?.status === "ERROR"
            ? screenRecordStatusText(latest, t)
            : providerEnabled === false
              ? t("Name screening is not configured")
              : "";

  return (
    <div ref={rootRef} id="client-name-screening">
    <DueDiligencePanel
      title={t("Name Screening")}
      badge={
        badge ? (
          <DueDiligenceBadge color={badge.color}>{badge.label}</DueDiligenceBadge>
        ) : null
      }
      note={note}
      noteColor={pendingHit ? "orange" : passed ? "green" : "gray"}
    >
      <Tabs value={section} onTabChange={setSection}>
        <Tabs.List>
          <Tabs.Tab value="screen">{t("Screen")}</Tabs.Tab>
          <Tabs.Tab value="matching">{t("Handle Match")}</Tabs.Tab>
          <Tabs.Tab value="result">{t("Result")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="screen" pt="lg" pb="xs">
          <Stack spacing="lg">
            {recorded.length ? (
              <Table
                fontSize="xs"
                verticalSpacing="xs"
                highlightOnHover
                sx={(theme) => ({
                  "tbody tr": { cursor: "pointer" },
                  "tbody tr[data-selected]": {
                    backgroundColor:
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[5]
                        : theme.colors.gray[0],
                  },
                  "tbody td": { verticalAlign: "top" },
                })}
              >
                <thead>
                  <tr>
                    <th>{t("Screened at")}</th>
                    <th>{t("Log ID")}</th>
                    <th>{t("Alert ID")}</th>
                    <th>{t("Type")}</th>
                    <th>{t("Name reference")}</th>
                    <th>{t("Status")}</th>
                    <th>{t("Result file")}</th>
                    <th>{t("Resolved at")}</th>
                    <th style={{ width: 44 }}>{t("Action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {recorded.map(({ row, index }) => {
                    const pendingHandle =
                      row.outcome.status === "HIT" && row.outcome.handlingStatus !== "RESOLVED";
                    const resolvedHandle =
                      row.outcome.status === "HIT" && row.outcome.handlingStatus === "RESOLVED";
                    const report = row.outcome.report;
                    const fileName = report?.fileName || report?.file?.name || "";
                    const fileReady = canDownloadMatchReportFile(report);
                    return (
                    <tr
                      key={`${row.outcome.logId}-${row.outcome.screenedAt}-${index}`}
                      data-selected={selectedEntry?.index === index ? "true" : undefined}
                      onClick={() => setSelectedIndex(index)}
                    >
                      <td>{formatScreenedAt(row.outcome.screenedAt)}</td>
                      <td>{row.outcome.logId}</td>
                      <td>{row.outcome.alertId || ""}</td>
                      <td>{t(nameScreeningType(row.outcome.type))}</td>
                      <td>{row.label}</td>
                      <td>
                        <Group spacing={6} noWrap align="center">
                          {pendingHandle ? (
                            <IconAlertTriangle
                              size={14}
                              color="#f08c00"
                              style={{ flexShrink: 0, display: "block" }}
                              aria-label={t("Pending Handle")}
                            />
                          ) : resolvedHandle ? (
                            <IconCircleCheck
                              size={14}
                              color="#2f9e44"
                              style={{ flexShrink: 0, display: "block" }}
                              aria-label={t("Resolved")}
                            />
                          ) : null}
                          <Text size="xs" sx={{ lineHeight: 1.4 }}>
                            {pendingHandle ? t("Pending Handle") : screenRecordStatusText(row, t)}
                          </Text>
                        </Group>
                      </td>
                      <td onClick={(event) => event.stopPropagation()}>
                        {fileName && fileReady ? (
                          <Anchor
                            component="button"
                            type="button"
                            size="xs"
                            onClick={() => downloadReport(report)}
                            sx={{
                              maxWidth: 180,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor:
                                downloadingLogId === String(report?.file?.link || fileName)
                                  ? "wait"
                                  : "pointer",
                            }}
                          >
                            {fileName}
                          </Anchor>
                        ) : fileName ? (
                          <Text size="xs" color="dimmed">
                            {fileName}
                          </Text>
                        ) : null}
                      </td>
                      <td>
                        {resolvedHandle ? formatScreenedAt(row.outcome.resolvedAt) : ""}
                      </td>
                      <td onClick={(event) => event.stopPropagation()}>
                        <Menu withinPortal position="bottom-end" shadow="md">
                          <Menu.Target>
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              aria-label={t("Actions")}
                            >
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            {row.outcome.status === "HIT" ? (
                              <Menu.Item
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setHandleIndex(index);
                                  setSection("matching");
                                }}
                              >
                                {t("Handle")}
                              </Menu.Item>
                            ) : null}
                            <Menu.Item
                              color="red"
                              onClick={(event) => {
                                event.stopPropagation();
                                removeRecord(index);
                              }}
                            >
                              {t("Remove")}
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : null}
            {selectedEntry ? (
              <Text size="sm" sx={{ lineHeight: 1.6 }}>
                {formatRow(selectedEntry.row, t)}
              </Text>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              loading={screening}
              disabled={providerEnabled !== true}
              onClick={screen}
              sx={{ alignSelf: "flex-start" }}
            >
              {t("Screen")}
            </Button>
          </Stack>
        </Tabs.Panel>
      </Tabs>
      {section !== "screen" && activeHandle ? (
        <ClientDueDiligenceMatchReport
          key={`${activeHandle.row.label}-${activeHandle.index}-${activeHandle.row.outcome.logId}`}
          view={section}
          report={activeHandle.row.outcome.report}
          form={form}
          client={form?.values}
          clientName={activeHandle.row.label}
          subjectLabel={activeHandle.row.label}
          clientId={form?.values?._id || form?.values?.id}
          alertId={activeHandle.row.outcome.alertId}
          logId={activeHandle.row.outcome.logId}
          screenedAt={activeHandle.row.outcome.screenedAt}
          resolvedAt={
            section === "matching" && handlingResolved && activeHandle.row.outcome.resolvedAt
              ? formatScreenedAt(activeHandle.row.outcome.resolvedAt)
              : ""
          }
          toolbar={
            section === "matching" ? (
              <>
                <Select
                  size="sm"
                  label={t("Screen record")}
                  aria-label={t("Screen record")}
                  data={hitEntries.map(({ row, index }) => ({
                    value: String(index),
                    label: handleMatchLabel(row, { includeName: includeHandleName }),
                  }))}
                  value={String(activeHandle.index)}
                  onChange={(value) => {
                    if (value != null) setHandleIndex(Number(value));
                  }}
                  sx={{ width: includeHandleName ? 460 : 280 }}
                />
                <Button
                  size="sm"
                  color={handlingResolved ? "red" : "green"}
                  onClick={() => setHandling(activeHandle.index, !handlingResolved)}
                >
                  {handlingResolved ? t("Mark pending") : t("Mark resolved")}
                </Button>
              </>
            ) : null
          }
          highlightPending={nameScreeningType(activeHandle.row.outcome.type) === "On-going"}
          onChange={(report) => updateReport(activeHandle.index, report)}
        />
      ) : null}
      {section !== "screen" && !activeHandle ? (
        <Text size="sm" color="dimmed" mt="sm">
          {t("No match to handle")}
        </Text>
      ) : null}
    </DueDiligencePanel>
    </div>
  );
};

const formatRow = (row, t) => {
  if (!row.ready || !row.outcome) {
    return [row.label, screenRecordStatusText(row, t)].filter(Boolean).join(" · ");
  }
  if (row.outcome.status === "ERROR") {
    const code = row.outcome.errorCode
      ? `${t("Error code")}: ${row.outcome.errorCode}`
      : "";
    return [row.label, code, row.outcome.errorMessage].filter(Boolean).join(" · ");
  }
  const result = screenRecordStatusText(row, t);
  const screenedOn = formatScreenedAt(row.outcome.screenedAt);
  return [
    row.label,
    result,
    `${t("Log ID")}: ${row.outcome.logId}`,
    row.outcome.alertId ? `${t("Alert ID")}: ${row.outcome.alertId}` : "",
    screenedOn ? `${t("Screened at")}: ${screenedOn}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
};

export default ClientDueDiligenceNameScreening;
